/**
 * usePayDataFetching Hook
 * Manages pay data fetching with proper dependencies
 */

import { useEffect, useCallback } from 'react';
import moment from 'moment';
import { getPracticeList } from '../../../redux/modules/practices';
import { getPayData, getPreviousPayData } from '../../../redux/modules/payReports';
import { getMonthRange } from '../utils/dateUtils';
import { getDoctorId, getDoctorFullName, getDoctorDetailsByAbbr } from '../services/doctorDataService';
import { dentistTypes } from '../../../constants';

/**
 * Custom hook for fetching pay data
 * @param {Object} user - Current user object
 * @param {string} startDate - Start date for data fetch
 * @param {Function} dispatch - Redux dispatch function
 * @param {boolean} saved - Is this a saved report view
 * @returns {Object} { fetchPayData }
 */
export const usePayDataFetching = (user, startDate, dispatch, saved = false) => {

  /**
   * Fetch practice list on mount
   */
  useEffect(() => {
    dispatch(getPracticeList());
  }, [dispatch]);

  /**
   * Fetch billing data for a specific date range
   * @param {Object} params - Optional parameters to override defaults
   */
  const fetchPayData = useCallback((params = {}) => {
    if (!user || !user.UdaDrList) return;

    const effectiveStartDate = params.startDate || startDate || moment().startOf('month').format('YYYY-MM-DD');
    const { endDate } = getMonthRange(effectiveStartDate);
    const source = params.source || 'DrPay';
    const dentistId = getDoctorId(user, user.UdaDrList.abbr, dentistTypes);

    if (!dentistId) return;

    let payParams = {
      startDate: effectiveStartDate,
      endDate: endDate,
      abbr: user.UdaDrList.abbr,
      filters: {
        dentistType: 2,
        years: moment(effectiveStartDate).format("YYYY"),
        months: [moment(effectiveStartDate).format("MM")],
        keyword: "",
        dentist: false,
        dentistId: dentistId,
      },
      sortBy: {},
      lock: {
        action: null,
      },
      source: source,
    };

    // Add doctor name if provided in params
    if (params.doctorName) {
      payParams.doctorName = params.doctorName;
    } else if (user.UdaDrList.abbr) {
      const doctorDetails = getDoctorDetailsByAbbr(user, dentistTypes, user.UdaDrList.abbr);
      payParams.doctorName = getDoctorFullName(doctorDetails);
    }

    // Merge any additional params
    payParams = { ...payParams, ...params };

    // Fetch previous pay data first
    dispatch(getPreviousPayData(payParams));

    // Then fetch current pay data
    const payParamsString = JSON.stringify(payParams);
    dispatch(getPayData(payParamsString, saved === true ? 'current' : saved));
  }, [user, startDate, saved, dispatch]);

  /**
   * Fetch initial data on mount or when dependencies change
   */

  return { fetchPayData, };
};
